import App from '$/api/api';
import { canvasRequest } from '$/api/canvasRquest';
import { checkMeiyeAmount } from '$/api/server';
import { roboneoApi } from '$/api/request';
import { openInsufficientRadishModal } from '$/store/useVipModalStore';
import Api from '@/apiSdk/api';
import type {
  AiToolQueryResponse,
  AiToolSubmitParamWithCategory,
  AiToolSubmitParamsFor,
  CreateAiToolTaskIds,
  TaskCategory,
} from './types';

export interface AiToolCostItem {
  id: string;
  tool_name: string;
  media_num?: number;
  video_duration?: number;
  size: string;
  quality?: string;
  resolution: string;
  params?: Record<string, any> & {
    task_category?: TaskCategory | 'video_watermark';
    video_duration?: number;
  };
}

export interface AiToolCostResponseItem {
  id: string;
  real_cost: number;
  model_version: string;
  fallback_cost: number;
  cost: number;
}

export const AI_TOOL_INSUFFICIENT_RADISH_ERROR = 'insufficient_credits';

/**
 * AI 工具创建任务前实时校验可用萝卜，余额为 0 时直接中断 submit.json。
 */
async function ensureAiToolSubmitRadishAvailable() {
  const data = await checkMeiyeAmount();
  if (Number(data?.amount || 0) > 0) return;

  openInsufficientRadishModal('func_token_short');
  throw new Error(AI_TOOL_INSUFFICIENT_RADISH_ERROR);
}

/**
 * 创建AI工具任务。
 * 新接口要求每个 params item 内部携带 task_category，因此这里兼容旧调用并写入 item JSON。
 *
 * @param task_category 兼容旧调用的任务类型；item 已有 task_category 时优先使用 item 自身类型
 * @param params id -> 任务参数 的映射（发送时 value 会 stringify）
 */
export async function createAiToolTask<T extends TaskCategory>(
  task_category: T | undefined,
  params: Record<string, AiToolSubmitParamsFor<T> | AiToolSubmitParamWithCategory>,
) {
  try {
    await ensureAiToolSubmitRadishAvailable();

    const paramsStringified = Object.fromEntries(
      Object.entries(params).map(([id, task]) => {
        const itemTaskCategory = 'task_category' in task ? task.task_category : task_category;
        return [id, JSON.stringify({
          task_category: itemTaskCategory,
          ...task,
        })];
      }),
    );
    const data = { params: paramsStringified };

    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      data,
      url: Api.roboneo.api.aiToolSubmit,
      method: 'POST',
    });
    return response.data as { data: { ids: CreateAiToolTaskIds; }; };
  } catch (error) {
    if (error instanceof Error && error.message === AI_TOOL_INSUFFICIENT_RADISH_ERROR) {
      throw error;
    }
    console.error('Proxy Test Error:', error);
    throw error;
  }
}

/**
 * 查询AI工具任务
 * @param ids 任务 ID，多个用英文逗号分隔的字符串
 */
export async function queryAiToolTask(ids: string) {
  try {
    const response = await canvasRequest.request({
      baseURL: App.roboneo.baseCanvasURL(),
      url: Api.roboneo.api.aiToolQuery,
      params: { ids },
      method: 'GET',
    });
    return response.data as { data: AiToolQueryResponse; };
  } catch (error) {
    console.error('Proxy Test Error:', error);
    throw error;
  }
}

/**
 * 动态计算 AI 工具成本。
 * 服务端负责按工具、参数和图片数量计算价格，前端只展示/校验返回值。
 */
export async function countAiToolCost(params: {
  room_id: string;
  items: AiToolCostItem[];
}) {
  try {
    const response = await roboneoApi.request({
      data: {
        parameter: {
          path_scene: 'countcost',
          ...params,
        },
      },
      method: 'POST',
      url: App.roboneo.api.path,
    });
    if (response.data?.error_code !== 0) {
      throw response.data;
    }
    return response.data.parameter as { items: AiToolCostResponseItem[]; };
  } catch (error) {
    console.error('Count AI tool cost error:', error);
    throw error;
  }
}
